import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { vehicleRouteService, vehicleService } from "../../../../services/vehicleRouteService"
import { useToast } from "../../../../hooks/use-toast"
import { v4 as uuidv4 } from 'uuid'
import axiosInstance from "../../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../../constants/config"

export function useVehicleRoute() {
  const [vehicleRoute, setVehicleRoute] = useState(null)
  const [vehicle, setVehicle] = useState(null)
  const [selectedShift, setSelectedShift] = useState(null)
  const [editingShift, setEditingShift] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [availableStudents, setAvailableStudents] = useState([])
  const [studentModalOpen, setStudentModalOpen] = useState(false)
  const [selectedStop, setSelectedStop] = useState(null)
  const [lastStopLocation, setLastStopLocation] = useState(null)
  const [shiftTypeModalOpen, setShiftTypeModalOpen] = useState(false)
  const [newShiftType, setNewShiftType] = useState('pickup')
  const [deleteShiftModalOpen, setDeleteShiftModalOpen] = useState(false)
  const [shiftToDelete, setShiftToDelete] = useState(null)
  const [copyShiftModalOpen, setCopyShiftModalOpen] = useState(false)
  const [shiftToCopy, setShiftToCopy] = useState(null)
  const [originalShiftName, setOriginalShiftName] = useState(null)
  const [currentLocationStopId, setCurrentLocationStopId] = useState(null)
  const [addingStopShiftId, setAddingStopShiftId] = useState(null)
  const [deletingStopId, setDeletingStopId] = useState(null)
  const [deletingShiftId, setDeletingShiftId] = useState(null)

  const { id: vehicleId } = useParams()
  const { toast } = useToast()

  const user = JSON.parse(localStorage.getItem("user") || "{}")
  const schoolId = user.school

  useEffect(() => {
    if (vehicleId) loadVehicle()
  }, [vehicleId])

  useEffect(() => {
    if (vehicleRoute?._id) fetchLastStopLocation()
    // eslint-disable-next-line
  }, [vehicleRoute?._id, selectedShift])

  const loadVehicle = async () => {
    try {
      setLoading(true)
      const response = await vehicleService.getVehicleById(vehicleId)
      const vehicleData = response.data.vehicle
      setVehicle(vehicleData)

      const routesResponse = await vehicleRouteService.getVehicleRoutes(schoolId)
      const existingRoute = routesResponse.data.vehicleRoutes.find(
        route => route.vehicle && route.vehicle._id === vehicleId
      )

      if (existingRoute) {
        setVehicleRoute(existingRoute)
        if (existingRoute.shifts.length > 0) {
          setSelectedShift(existingRoute.shifts[0]._id)
        }
      } else {
        setVehicleRoute({
          vehicle: vehicleData._id,
          school: schoolId,
          shifts: [],
          isActive: true
        })
      }

      await loadAvailableStudents()
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to load vehicle",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableStudents = async () => {
    try {
      const response = await vehicleRouteService.getAvailableStudents(schoolId)
      setAvailableStudents(response.data.students || [])
    } catch (error) {
      console.error("Error loading students:", error)
    }
  }

  const saveVehicleRoute = async () => {
    try {
      setSaving(true)
      toast({ title: "Saving...", description: "Updating vehicle route and rebuilding polyline" })

      if (vehicleRoute._id) {
        const response = await vehicleRouteService.updateVehicleRoute(vehicleRoute._id, {
          shifts: vehicleRoute.shifts,
          driver: vehicleRoute.driver,
          conductor: vehicleRoute.conductor,
          capacity: vehicleRoute.capacity,
          isActive: vehicleRoute.isActive
        })

        if (response.data?.vehicleRoute) {
          setVehicleRoute(response.data.vehicleRoute)
          if (response.data.vehicleRoute.shifts.length > 0) {
            const shiftStillExists = response.data.vehicleRoute.shifts.some(s => s._id === selectedShift)
            if (!shiftStillExists) setSelectedShift(response.data.vehicleRoute.shifts[0]._id)
          }
        }

        toast({ title: "Success", description: "Vehicle route and polyline updated successfully" })
      } else {
        const response = await vehicleRouteService.createVehicleRoute({
          vehicleId,
          schoolId,
          shifts: vehicleRoute.shifts,
          driver: vehicleRoute.driver,
          conductor: vehicleRoute.conductor,
          capacity: vehicleRoute.capacity
        })
        setVehicleRoute(response.data.vehicleRoute)
        if (response.data.vehicleRoute.shifts.length > 0) {
          setSelectedShift(response.data.vehicleRoute.shifts[0]._id)
        }
        toast({ title: "Success", description: "Vehicle route created successfully with accurate road-based polyline" })
      }
    } catch (error) {
      console.error("Error saving vehicle route:", error)
      const errorMessage = error.response?.data?.message || "Failed to save vehicle route"
      const errorHint = error.response?.data?.hint || ""
      toast({
        title: "Error",
        description: errorHint ? `${errorMessage}\n\nHint: ${errorHint}` : errorMessage,
        variant: "destructive",
        duration: 8000
      })
    } finally {
      setSaving(false)
    }
  }

  const handleAddShift = () => {
    setShiftTypeModalOpen(true)
  }

  const handleCreateShift = async () => {
    try {
      const newShift = {
        name: "New Shift",
        type: newShiftType,
        startTime: "07:00",
        endTime: "09:00",
        stops: []
      }

      if (vehicleRoute._id) {
        const response = await vehicleRouteService.addShift(vehicleRoute._id, newShift)
        const updatedRoute = response.data.vehicleRoute
        setVehicleRoute(updatedRoute)
        setSelectedShift(updatedRoute.shifts[updatedRoute.shifts.length - 1]._id)
      } else {
        const newShifts = [...vehicleRoute.shifts, { ...newShift, _id: `temp-${Date.now()}` }]
        setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
        setSelectedShift(newShifts[newShifts.length - 1]._id)
      }

      setShiftTypeModalOpen(false)
      setNewShiftType('pickup')
    } catch (error) {
      console.error("Error adding shift:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to add shift",
        variant: "destructive"
      })
    }
  }

  const handleCopyShiftClick = (shiftId) => {
    const shift = vehicleRoute.shifts.find(s => s._id === shiftId)
    if (shift && shift.stops && shift.stops.length > 0) {
      setShiftToCopy(shift)
      setCopyShiftModalOpen(true)
    }
  }

  const handleCopyShiftConfirmed = async () => {
    if (!shiftToCopy) return

    try {
      const sourceShift = shiftToCopy
      const targetType = sourceShift.type === 'pickup' ? 'drop' : 'pickup'

      if (!sourceShift.stops || sourceShift.stops.length === 0) {
        toast({ title: "Error", description: "Source shift has no stops to copy", variant: "destructive" })
        setCopyShiftModalOpen(false)
        setShiftToCopy(null)
        return
      }

      const reversedStops = [...sourceShift.stops]
        .reverse()
        .map((stop, index) => ({
          name: stop.name,
          latitude: stop.latitude,
          longitude: stop.longitude,
          students: [],
          order: index,
          isCheckpoint: stop.isCheckpoint || false
        }))

      const existingTargetShift = vehicleRoute.shifts.find(shift => shift.type === targetType)

      if (existingTargetShift) {
        if (vehicleRoute._id) {
          for (const stop of [...existingTargetShift.stops]) {
            try {
              await vehicleRouteService.deleteStop(vehicleRoute._id, existingTargetShift._id, stop._id)
            } catch (error) {
              console.error("Error deleting stop:", error)
            }
          }
          for (const stop of reversedStops) {
            try {
              await vehicleRouteService.addStop(vehicleRoute._id, existingTargetShift._id, stop)
            } catch (error) {
              console.error("Error adding stop:", error)
            }
          }
          const routesResponse = await vehicleRouteService.getVehicleRoutes(schoolId)
          const existingRoute = routesResponse.data.vehicleRoutes.find(
            route => route.vehicle && route.vehicle._id === vehicleId
          )
          if (existingRoute) {
            setVehicleRoute(existingRoute)
            setSelectedShift(existingTargetShift._id)
          }
        } else {
          const newShifts = vehicleRoute.shifts.map(shift => {
            if (shift._id === existingTargetShift._id) {
              return { ...shift, stops: reversedStops.map(stop => ({ ...stop, _id: uuidv4() })) }
            }
            return shift
          })
          setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
          setSelectedShift(existingTargetShift._id)
        }

        toast({
          title: "Success",
          description: `${targetType.charAt(0).toUpperCase() + targetType.slice(1)} shift updated with ${reversedStops.length} stops (reversed order)`
        })
      } else {
        const newShift = {
          name: `${targetType.charAt(0).toUpperCase() + targetType.slice(1)} Shift`,
          type: targetType,
          startTime: targetType === 'drop' ? "15:00" : "07:00",
          endTime: targetType === 'drop' ? "17:00" : "09:00",
          stops: reversedStops
        }

        if (vehicleRoute._id) {
          const response = await vehicleRouteService.addShift(vehicleRoute._id, {
            name: newShift.name,
            type: newShift.type,
            startTime: newShift.startTime,
            endTime: newShift.endTime
          })
          const updatedRoute = response.data.vehicleRoute
          const newShiftId = updatedRoute.shifts[updatedRoute.shifts.length - 1]._id

          for (const stop of reversedStops) {
            try {
              await vehicleRouteService.addStop(vehicleRoute._id, newShiftId, stop)
            } catch (error) {
              console.error("Error adding stop:", error)
            }
          }

          const routesResponse = await vehicleRouteService.getVehicleRoutes(schoolId)
          const existingRoute = routesResponse.data.vehicleRoutes.find(
            route => route.vehicle && route.vehicle._id === vehicleId
          )
          if (existingRoute) {
            setVehicleRoute(existingRoute)
            setSelectedShift(newShiftId)
          }
        } else {
          const newShifts = [
            ...vehicleRoute.shifts,
            { ...newShift, _id: `temp-${Date.now()}`, stops: reversedStops.map(stop => ({ ...stop, _id: uuidv4() })) }
          ]
          setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
          setSelectedShift(newShifts[newShifts.length - 1]._id)
        }

        toast({
          title: "Success",
          description: `${targetType.charAt(0).toUpperCase() + targetType.slice(1)} shift created with ${reversedStops.length} stops (reversed order)`
        })
      }

      setCopyShiftModalOpen(false)
      setShiftToCopy(null)
    } catch (error) {
      console.error("Error copying shift:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to copy shift",
        variant: "destructive"
      })
      setCopyShiftModalOpen(false)
      setShiftToCopy(null)
    }
  }

  const handleShiftTypeChange = async (shiftId, newType) => {
    try {
      const newShifts = vehicleRoute.shifts.map(shift =>
        shift._id === shiftId ? { ...shift, type: newType } : shift
      )

      if (vehicleRoute._id) {
        await vehicleRouteService.updateShift(vehicleRoute._id, shiftId, { type: newType })
      }
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
    } catch (error) {
      console.error("Error updating shift type:", error)
      if (error.response?.data?.message?.includes("cannot be changed after route creation")) {
        toast({
          title: "Cannot Change Shift Type",
          description: "Shift type (pickup/drop) cannot be changed after route creation. Please delete and recreate the shift if needed.",
          variant: "destructive"
        })
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.message || "Failed to update shift type",
          variant: "destructive"
        })
      }
    }
  }

  const handleDeleteShift = async (shiftId) => {
    try {
      if (vehicleRoute.shifts.length === 1) {
        toast({ title: "Error", description: "Cannot delete the last shift", variant: "destructive" })
        return
      }

      const newShifts = vehicleRoute.shifts.filter(s => s._id !== shiftId)
      const newSelectedShift = selectedShift === shiftId ? newShifts[0]._id : selectedShift

      if (vehicleRoute._id) {
        await vehicleRouteService.deleteShift(vehicleRoute._id, shiftId)
      }
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
      setSelectedShift(newSelectedShift)
    } catch (error) {
      console.error("Error deleting shift:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete shift",
        variant: "destructive"
      })
    }
  }

  const handleDeleteShiftConfirm = (shift) => {
    setShiftToDelete(shift)
    setDeleteShiftModalOpen(true)
  }

  const handleDeleteShiftConfirmed = async () => {
    if (!shiftToDelete) return
    setDeletingShiftId(shiftToDelete._id)
    try {
      await handleDeleteShift(shiftToDelete._id)
      setDeleteShiftModalOpen(false)
      setShiftToDelete(null)
      toast({ title: "Success", description: `Shift "${shiftToDelete.name}" deleted successfully` })
    } finally {
      setDeletingShiftId(null)
    }
  }

  const onDragEnd = async (result, shiftId) => {
    if (!result.destination) return

    try {
      const cleanShiftId = String(shiftId).trim().split(':')[0]
      const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === cleanShiftId || s._id === shiftId)

      if (shiftIdx === -1) {
        console.error("Shift not found:", cleanShiftId)
        return
      }

      const stops = Array.from(vehicleRoute.shifts[shiftIdx].stops)
      const [removed] = stops.splice(result.source.index, 1)
      stops.splice(result.destination.index, 0, removed)

      const newShifts = [...vehicleRoute.shifts]
      newShifts[shiftIdx] = { ...newShifts[shiftIdx], stops }
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })

      if (vehicleRoute._id) {
        const actualShiftId = vehicleRoute.shifts[shiftIdx]._id
        const stopIds = stops.map(stop => stop._id).filter(Boolean)
        await vehicleRouteService.updateStopOrder(vehicleRoute._id, actualShiftId, stopIds)
      }
    } catch (error) {
      console.error("Error updating stop order:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update stop order",
        variant: "destructive"
      })
    }
  }

  const handleAddStop = async (shiftId) => {
    const newStop = { name: "New Stop", latitude: "0.0", longitude: "0.0", students: [], isCheckpoint: false }

    if (vehicleRoute._id) {
      setAddingStopShiftId(shiftId)
      try {
        const response = await vehicleRouteService.addStop(vehicleRoute._id, shiftId, newStop)
        const updatedRoute = response?.data?.vehicleRoute ?? response?.vehicleRoute
        if (updatedRoute) {
          setVehicleRoute(updatedRoute)
        } else {
          const routesRes = await vehicleRouteService.getVehicleRoutes(schoolId)
          const route = routesRes.data.vehicleRoutes.find(r => r.vehicle?._id === vehicleId)
          if (route) setVehicleRoute(route)
        }
      } catch (error) {
        console.error("Error adding stop:", error)
        toast({
          title: "Error",
          description: error.response?.data?.message || "Failed to add stop",
          variant: "destructive"
        })
      } finally {
        setAddingStopShiftId(null)
      }
    } else {
      const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
      const newShifts = [...vehicleRoute.shifts]
      newShifts[shiftIdx].stops.push({ ...newStop, _id: uuidv4() })
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
    }
  }

  const handleDeleteStop = async (shiftId, stopId) => {
    if (vehicleRoute._id) {
      setDeletingStopId(stopId)
      try {
        const response = await vehicleRouteService.deleteStop(vehicleRoute._id, shiftId, stopId)
        const updatedRoute = response?.data?.vehicleRoute ?? response?.vehicleRoute
        if (updatedRoute) {
          setVehicleRoute(updatedRoute)
        } else {
          const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
          const newShifts = [...vehicleRoute.shifts]
          newShifts[shiftIdx].stops = newShifts[shiftIdx].stops.filter(s => s._id !== stopId)
          setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
        }
      } catch (error) {
        console.error("Error deleting stop:", error)
        toast({
          title: "Error",
          description: error.response?.data?.message || "Failed to delete stop",
          variant: "destructive"
        })
      } finally {
        setDeletingStopId(null)
      }
    } else {
      const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
      const newShifts = [...vehicleRoute.shifts]
      newShifts[shiftIdx].stops = newShifts[shiftIdx].stops.filter(s => s._id !== stopId)
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
    }
  }

  const handleCopyStop = async (shiftId, stop) => {
    try {
      const newStop = {
        ...stop,
        name: `${stop.name} (Copy)`,
        students: [...stop.students],
        isCheckpoint: stop.isCheckpoint || false
      }

      if (vehicleRoute._id) {
        const response = await vehicleRouteService.addStop(vehicleRoute._id, shiftId, newStop)
        setVehicleRoute(response.data.vehicleRoute)
      } else {
        const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
        const newShifts = [...vehicleRoute.shifts]
        newShifts[shiftIdx].stops.push({ ...newStop, _id: uuidv4() })
        setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
      }
    } catch (error) {
      console.error("Error copying stop:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to copy stop",
        variant: "destructive"
      })
    }
  }

  const handleEditStop = async (shiftId, stopId, field, value) => {
    try {
      const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
      const newShifts = [...vehicleRoute.shifts]
      newShifts[shiftIdx].stops = newShifts[shiftIdx].stops.map(s =>
        s._id === stopId ? { ...s, [field]: value } : s
      )
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })

      if (vehicleRoute._id) {
        await vehicleRouteService.updateStop(vehicleRoute._id, shiftId, stopId, { [field]: value })
      }
    } catch (error) {
      console.error("Error updating stop:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update stop",
        variant: "destructive"
      })
    }
  }

  const handlePasteCoordinates = async (shiftId, stopId) => {
    try {
      const text = await navigator.clipboard.readText()
      const coordPattern = /(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)/
      const match = text.match(coordPattern)

      if (match && match.length >= 3) {
        const lat = match[1].trim()
        const lng = match[2].trim()
        const latNum = parseFloat(lat)
        const lngNum = parseFloat(lng)

        if (isNaN(latNum) || isNaN(lngNum)) {
          toast({ title: "Invalid Format", description: "Could not parse coordinates. Please copy coordinates in format: lat, lng", variant: "destructive" })
          return
        }
        if (latNum < -90 || latNum > 90) {
          toast({ title: "Invalid Latitude", description: "Latitude must be between -90 and 90", variant: "destructive" })
          return
        }
        if (lngNum < -180 || lngNum > 180) {
          toast({ title: "Invalid Longitude", description: "Longitude must be between -180 and 180", variant: "destructive" })
          return
        }

        const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
        const newShifts = [...vehicleRoute.shifts]
        newShifts[shiftIdx].stops = newShifts[shiftIdx].stops.map(s =>
          s._id === stopId ? { ...s, latitude: lat, longitude: lng } : s
        )
        setVehicleRoute({ ...vehicleRoute, shifts: newShifts })

        if (vehicleRoute._id) {
          await vehicleRouteService.updateStop(vehicleRoute._id, shiftId, stopId, { latitude: lat, longitude: lng })
        }

        toast({ title: "Success", description: "Coordinates pasted successfully" })
      } else {
        toast({
          title: "Invalid Format",
          description: "Could not find coordinates. Please copy coordinates in format: lat, lng (e.g., 28.195, 76.798)",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error("Error pasting coordinates:", error)
      toast({
        title: "Error",
        description: "Failed to read clipboard. Please make sure you have copied the coordinates.",
        variant: "destructive"
      })
    }
  }

  const handleSetCurrentLocation = (shiftId, stopId) => {
    if (!navigator.geolocation) {
      toast({ title: "Not supported", description: "Geolocation is not supported by your browser.", variant: "destructive" })
      return
    }
    setCurrentLocationStopId(stopId)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = String(position.coords.latitude)
        const lng = String(position.coords.longitude)
        const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === shiftId)
        const newShifts = [...vehicleRoute.shifts]
        newShifts[shiftIdx].stops = newShifts[shiftIdx].stops.map(s =>
          s._id === stopId ? { ...s, latitude: lat, longitude: lng } : s
        )
        setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
        if (vehicleRoute._id) {
          vehicleRouteService.updateStop(vehicleRoute._id, shiftId, stopId, { latitude: lat, longitude: lng })
            .catch(error => {
              console.error("Error updating stop:", error)
              toast({ title: "Error", description: error.response?.data?.message || "Failed to save coordinates", variant: "destructive" })
            })
        }
        toast({ title: "Success", description: "Current location set as coordinates" })
        setCurrentLocationStopId(null)
      },
      (err) => {
        setCurrentLocationStopId(null)
        const message =
          err.code === 1 ? "Location permission denied. Allow location access and try again." :
          err.code === 2 ? "Location unavailable. Check your connection or try again." :
          "Could not get your location. Try again."
        toast({ title: "Location error", description: message, variant: "destructive" })
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  const handleEditShiftName = (shiftId, newName) => {
    const newShifts = vehicleRoute.shifts.map(shift =>
      shift._id === shiftId ? { ...shift, name: newName } : shift
    )
    setVehicleRoute({ ...vehicleRoute, shifts: newShifts })
  }

  const handleShiftNameBlur = async (shiftId, newName) => {
    try {
      if (vehicleRoute._id) {
        await vehicleRouteService.updateShift(vehicleRoute._id, shiftId, { name: newName })
      }
      setOriginalShiftName(null)
    } catch (error) {
      console.error("Error updating shift name:", error)
      if (originalShiftName !== null) {
        const revertedShifts = vehicleRoute.shifts.map(shift =>
          shift._id === shiftId ? { ...shift, name: originalShiftName } : shift
        )
        setVehicleRoute({ ...vehicleRoute, shifts: revertedShifts })
      }
      setOriginalShiftName(null)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update shift name",
        variant: "destructive"
      })
    }
  }

  const handleOpenStudentModal = (stop, currentShift) => {
    setSelectedStop({ ...stop, shiftType: currentShift?.type || 'pickup' })
    setStudentModalOpen(true)
  }

  const handleStudentsUpdated = async (studentIds, currentShift) => {
    if (!selectedStop || !currentShift) return
    try {
      const shiftIdx = vehicleRoute.shifts.findIndex(s => s._id === currentShift._id)
      const newShifts = [...vehicleRoute.shifts]
      const stopIdx = newShifts[shiftIdx].stops.findIndex(s => s._id === selectedStop._id)
      newShifts[shiftIdx].stops[stopIdx].students = studentIds
      setVehicleRoute({ ...vehicleRoute, shifts: newShifts })

      if (vehicleRoute._id) {
        await vehicleRouteService.updateStop(vehicleRoute._id, currentShift._id, selectedStop._id, { students: studentIds })
      }

      toast({ title: "Success", description: "Students updated successfully" })
    } catch (error) {
      console.error("Error updating students:", error)
      if (error.response?.data?.message === "Some students are already assigned to other routes") {
        const conflictingNames = error.response?.data?.conflictingStudentNames || []
        const conflictingStudents = availableStudents.filter(student =>
          conflictingNames.some(name => `${student.firstName} ${student.lastName}` === name)
        )
        const vehicleInfo = conflictingStudents.map(student =>
          `${student.firstName} ${student.lastName} (${student.assignedTo?.vehicleNumber || 'Unknown Vehicle'})`
        ).join(", ")
        toast({
          title: "Error",
          description: `The following students are already assigned to other routes: ${vehicleInfo}. Please select different students.`,
          variant: "destructive"
        })
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.message || "Failed to update students",
          variant: "destructive"
        })
      }
    }
  }

  const fetchLastStopLocation = async () => {
    if (!vehicleRoute?._id) return null
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.vehicleRouteLastStop(vehicleRoute._id))
      if (res.data && typeof res.data.lat === "number" && typeof res.data.lng === "number") {
        setLastStopLocation({ lat: res.data.lat, lng: res.data.lng })
        return { lat: res.data.lat, lng: res.data.lng }
      }
    } catch (e) {
      console.error("[DEBUG] GET last-stop-location API error:", e)
    }
    return null
  }

  const currentShift = vehicleRoute?.shifts.find(s => s._id === selectedShift)

  return {
    vehicleRoute,
    vehicle,
    selectedShift, setSelectedShift,
    editingShift, setEditingShift,
    loading, saving,
    availableStudents,
    studentModalOpen, setStudentModalOpen,
    selectedStop,
    lastStopLocation,
    shiftTypeModalOpen, setShiftTypeModalOpen,
    newShiftType, setNewShiftType,
    deleteShiftModalOpen, setDeleteShiftModalOpen,
    shiftToDelete,
    copyShiftModalOpen, setCopyShiftModalOpen,
    shiftToCopy,
    originalShiftName, setOriginalShiftName,
    currentLocationStopId,
    addingStopShiftId,
    deletingStopId,
    deletingShiftId,
    currentShift,
    saveVehicleRoute,
    handleAddShift,
    handleCreateShift,
    handleCopyShiftClick,
    handleCopyShiftConfirmed,
    handleShiftTypeChange,
    handleDeleteShiftConfirm,
    handleDeleteShiftConfirmed,
    onDragEnd,
    handleAddStop,
    handleDeleteStop,
    handleCopyStop,
    handleEditStop,
    handlePasteCoordinates,
    handleSetCurrentLocation,
    handleEditShiftName,
    handleShiftNameBlur,
    handleOpenStudentModal,
    handleStudentsUpdated,
  }
}
